import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Row, Col, Table, Button, Spinner, Alert, Modal, InputGroup } from 'react-bootstrap';
import CountyWorkspace from '../components/CountyWorkspace';
import Pagination from '../components/Pagination';
import StatusBadge from '../components/StatusBadge';
import { adminApi, extractError } from '../api/client';
import { fmtRelative, fmtDate } from '../lib/lookups';
import { useToast } from '../contexts/ToastContext';
import { getSectionConfig, DATA_STATUS_OPTIONS } from '../lib/countySections';

export default function CountyChildListPage({ section }) {
  const { stateId, countyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const cfg = getSectionConfig(section);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      const res = await adminApi.get(`/admin/states/${stateId}/counties/${countyId}/${cfg.apiPath}`, { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId, countyId, page, q, status, cfg.apiPath]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.delete(`/admin/states/${stateId}/counties/${countyId}/${cfg.apiPath}/${deleteTarget.id}`);
      toast.success(`${deleteTarget[cfg.nameField]} deleted`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  }

  function renderCell(item, col) {
    const v = item[col.key];
    if (!v) return <span className="text-muted">—</span>;
    if (col.key === 'website' || /url/i.test(col.key)) {
      return <a href={v} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>{v}</a>;
    }
    if (col.key === 'election_date' || /deadline|start|end/i.test(col.key)) {
      return v;
    }
    return v;
  }

  return (
    <CountyWorkspace
      stateId={stateId}
      countyId={countyId}
      activeSection={section}
      title={cfg.titlePlural}
      breadcrumbTrail={[{ label: cfg.titlePlural }]}
      actions={
        <Button variant="primary" onClick={() => navigate(`/admin/states/${stateId}/counties/${countyId}/${cfg.slug}/new`)} data-testid={`${cfg.slug}-create-button`}>
          <i className="bi bi-plus-lg me-1" /> New {cfg.titleSingular.toLowerCase()}
        </Button>
      }
    >
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col xs={12} md={6}>
              <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Name, notes…"
                  value={q}
                  onChange={(e) => { setPage(1); setQ(e.target.value); }}
                  data-testid={`${cfg.slug}-search-input`}
                />
              </InputGroup>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Status</Form.Label>
              <Form.Select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid={`${cfg.slug}-status-select`}>
                <option value="">All</option>
                {DATA_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        {loading ? (
          <div className="text-center p-5"><Spinner animation="border" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <i className={`bi ${cfg.icon}`} />
            <div>No {cfg.titlePlural.toLowerCase()} yet.</div>
            <Button variant="primary" className="mt-2" onClick={() => navigate(`/admin/states/${stateId}/counties/${countyId}/${cfg.slug}/new`)}>Add first record</Button>
          </div>
        ) : (
          <>
            <Table hover responsive size="sm" className="mb-0" data-testid={`${cfg.slug}-table`}>
              <thead>
                <tr>
                  {cfg.listColumns.map((c) => <th key={c.key}>{c.label}</th>)}
                  <th>Status</th>
                  <th>Last verified</th>
                  <th>Updated</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} data-testid={`${cfg.slug}-row-${item.id}`}>
                    {cfg.listColumns.map((c, idx) => (
                      <td key={c.key}>
                        {idx === 0 ? (
                          <Link to={`/admin/states/${stateId}/counties/${countyId}/${cfg.slug}/${item.id}`} className="text-decoration-none fw-semibold">
                            {item[c.key] || <span className="text-muted">(no name)</span>}
                          </Link>
                        ) : renderCell(item, c)}
                      </td>
                    ))}
                    <td><StatusBadge status={item.status} /></td>
                    <td className="text-muted">{item.last_verified_at ? fmtDate(item.last_verified_at) : <span className="text-muted">never</span>}</td>
                    <td className="text-muted">{fmtRelative(item.updated_at)}</td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <Link to={`/admin/states/${stateId}/counties/${countyId}/${cfg.slug}/${item.id}`}><Button size="sm" variant="outline-primary" data-testid={`${cfg.slug}-edit-${item.id}`}>Edit</Button></Link>
                        <Button size="sm" variant="outline-danger" onClick={() => setDeleteTarget(item)} data-testid={`${cfg.slug}-delete-${item.id}`}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3" style={{ borderTop: '1px solid var(--border)' }}>
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>Page {page} of {totalPages} · {total} records</div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </div>
            )}
          </>
        )}
      </Card>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton><Modal.Title>Delete {cfg.titleSingular.toLowerCase()}</Modal.Title></Modal.Header>
        <Modal.Body>Delete <strong>{deleteTarget?.[cfg.nameField]}</strong>? This cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting} data-testid={`confirm-delete-${cfg.slug}-button`}>
            {deleting ? <Spinner size="sm" animation="border" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </CountyWorkspace>
  );
}
